import React, { useEffect, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import posthog from 'posthog-js'
import { useAuthProvider } from '../../modules/Auth/providers/AuthSupabaseProvider/AuthProvider.hook'
import routes from '../../config/settings/routes'
import { brand } from '../../config/settings/brand'
import { toUserFacingError } from '../../config/resources/errors'
import { ACCOUNT_INACTIVE_REASON } from '../../modules/Auth/providers/services/authSupabase'
import AuthSplitLayout from '../AuthShell/AuthSplitLayout'
import GoogleLogo from '../AuthShell/GoogleLogo'
import {
  Title,
  Subtitle,
  GoogleButton,
  GoogleIcon,
  ErrorText,
  ErrorMailto,
  FooterNote,
  FooterLegal,
  FooterLegalLink,
} from '../AuthShell/AuthShell.styles'

const LoginSupabase = () => {
  const { signInWithGoogle, isAuthenticated, loading, error } = useAuthProvider()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const authError = useMemo(
    () => searchParams.get('authError'),
    [searchParams]
  )
  const isAccountInactive =
    searchParams.get('reason') === ACCOUNT_INACTIVE_REASON

  const displayError = isAccountInactive
    ? null
    : authError ||
      (error
        ? toUserFacingError(
            error?.message ?? String(error),
            'Tu sesión expiró. Vuelve a iniciar sesión e inténtalo de nuevo.'
          )
        : null)

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate(routes.dashboard, { replace: true })
    }
  }, [isAuthenticated, loading, navigate])

  const handleGoogleLogin = () => {
    if (authError || isAccountInactive) {
      const next = new URLSearchParams(searchParams)
      next.delete('authError')
      next.delete('reason')
      setSearchParams(next, { replace: true })
    }
    posthog.capture('login_attempted', { method: 'google' })
    signInWithGoogle()
  }

  return (
    <AuthSplitLayout
      brandEyebrow={brand.name}
      brandHeadline={brand.tagline}
      brandSupport="Tu monto al día, historial claro y avisos a tiempo — sin planillas."
    >
      <Title>Entra a {brand.name}</Title>
      <Subtitle>
        Continúa con Google para ver tu monto al día, con claridad y sin
        planillas.
      </Subtitle>

      <GoogleButton
        type="button"
        onClick={handleGoogleLogin}
        disabled={loading}
        aria-label="Continuar con Google"
      >
        <GoogleIcon>
          <GoogleLogo />
        </GoogleIcon>
        {loading ? 'Conectando…' : 'Continuar con Google'}
      </GoogleButton>

      {!loading && isAccountInactive && (
        <ErrorText>
          Tu cuenta está desactivada. Escribe a{' '}
          <ErrorMailto href={`mailto:${brand.legalContactEmail}`}>
            {brand.legalContactEmail}
          </ErrorMailto>
          .
        </ErrorText>
      )}
      {!loading && displayError && <ErrorText>{displayError}</ErrorText>}

      <FooterNote>
        Acceso con Google. No pedimos RUT ni datos bancarios.
      </FooterNote>
      <FooterLegal>
        Al continuar aceptas los{' '}
        <FooterLegalLink to={routes.terms}>Términos</FooterLegalLink> y la{' '}
        <FooterLegalLink to={routes.privacy}>
          Política de Privacidad
        </FooterLegalLink>
        .
      </FooterLegal>
    </AuthSplitLayout>
  )
}

export default LoginSupabase
